/**
 * Find your apron quiz (19 Sep 2026) for Ape the Label.
 * Markup and settings: sections/apron-quiz.liquid   Styles: assets/apron-quiz.css
 *
 * Three taps: role, priority and strap style. Each answer moves on by itself, then the shopper sees
 * one recommended set and one alternative (real bundle products with live price and stock).
 * - The back arrow and the phone's own back gesture step back through the questions.
 * - "Add set to cart" runs through the theme's own cart, so the drawer, upsells and error messages
 *   behave exactly like the rest of the store.
 * - "Save your match" subscribes the shopper to the Klaviyo list and saves their answers on their
 *   profile (Salon role, priority, straps, match), plus a "Completed Apron Quiz" event carrying the
 *   match for an email flow. Shoppers Klaviyo already recognises get their answers saved right away.
 */
(() => {
  if (customElements.get('apron-quiz')) return;

  const ADVANCE_DELAY = 280;
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const QUESTIONS = ['role', 'priority', 'strap'];
  const FAMILIES = {
    tpu_og: ['tpu', 'og'],
    tpu_everything: ['tpu', 'everything'],
    leather_og: ['leather', 'og'],
    leather_everything: ['leather', 'everything'],
    pinny: ['leather', 'pinny'],
  };
  const AFTERPAY_CURRENCIES = ['AUD', 'NZD'];
  const AFTERPAY_INFO = 'https://www.afterpay.com/en-AU/how-it-works';

  const designMode = () => Boolean(window.Shopify && window.Shopify.designMode);
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const currency = () => (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';

  const money = (cents) => {
    const theme = window.theme;
    if (theme && typeof theme.formatMoney === 'function') return theme.formatMoney(cents, theme.moneyFormat);
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency() || 'AUD' }).format(cents / 100);
  };
  // Savings read better rounded: "$20" rather than "$19.99"
  const roundMoney = (cents) => money(Math.round(cents / 100) * 100).replace(/[.,]00(?=\D*$)/, '');
  const fill = (text, values) => String(text || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
  const absolute = (url) => {
    try {
      return new URL(url, window.location.origin).href;
    } catch (error) {
      return url || '';
    }
  };
  const preload = (url) => {
    if (!url) return;
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
  };
  const klaviyoCompany = (settings) => {
    if (settings.company) return settings.company;
    const script = document.querySelector('script[src*="klaviyo.js?company_id="]');
    const match = script && script.src.match(/company_id=([^&]+)/);
    return match ? match[1] : '';
  };
  const klaviyoPost = (endpoint, company, data) =>
    fetch(`https://a.klaviyo.com/client/${endpoint}/?company_id=${encodeURIComponent(company)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', revision: '2025-04-15' },
      body: JSON.stringify({ data }),
    });

  class ApronQuiz extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      try {
        this.data = JSON.parse(this.querySelector('[data-quiz-data]').textContent);
      } catch (error) {
        return;
      }
      this.strings = this.data.strings || {};
      this.settings = this.data.settings || {};
      this.sets = (this.data.sets || [])
        .filter((set) => set.available && set.variant && FAMILIES[set.family])
        .map((set, index) => ({ ...set, index, material: FAMILIES[set.family][0], strap: FAMILIES[set.family][1] }));
      this.answers = {};
      this.result = null;
      this.step = 1;
      this.steps = [...this.querySelectorAll('[data-step]')];
      this.ui = {
        stepLabel: this.querySelector('[data-quiz-step-label]'),
        track: this.querySelector('[data-quiz-track]'),
        fill: this.querySelector('[data-quiz-fill]'),
        footer: this.querySelector('[data-quiz-footer]'),
        next: this.querySelector('[data-quiz-next]'),
        colours: this.querySelector('[data-quiz-colours]'),
        details: this.querySelector('[data-quiz-details]'),
        alt: this.querySelector('[data-quiz-alt]'),
        cart: this.querySelector('[data-quiz-cart]'),
        emailForm: this.querySelector('[data-quiz-email]'),
        emailDone: this.querySelector('[data-quiz-email-done]'),
        emailError: this.querySelector('[data-quiz-email-error]'),
        check: this.querySelector('[data-quiz-check]'),
      };

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.ui.cart.addEventListener('submit', (event) => this.addToCart(event));
      this.ui.emailForm.addEventListener('submit', (event) => this.submitEmail(event));
      this.ui.emailForm.elements.email.addEventListener('input', () => this.emailMessage(''));

      if (designMode()) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      } else {
        window.addEventListener('popstate', this.onPopState);
        history.replaceState({ ...(history.state || {}), apronQuiz: 1, apronQuizIndex: 0 }, '');
      }
      document.documentElement.classList.add('ape-quiz-page');
      this.updateChrome();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      clearTimeout(this.timer);
      document.documentElement.classList.remove('ape-quiz-page');
    }

    el(name) {
      return this.querySelector(`[data-r="${name}"]`);
    }

    /* ---------- Events ---------- */

    onClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const option = target.closest('.ape-quiz__option');
      if (option) return this.choose(option);
      if (target.closest('[data-quiz-next]')) return this.next();
      if (target.closest('[data-quiz-back]')) return this.back();
      if (target.closest('[data-quiz-close]')) return this.close(event);
      if (target.closest('[data-quiz-colours]')) return this.toggleColours();
      const swatch = target.closest('[data-set]');
      if (swatch) return this.showSet(this.sets[Number(swatch.dataset.set)], { keepColours: true });
      if (target.closest('[data-quiz-alt]')) return this.swapAlternative();
      if (target.closest('[data-quiz-retake]')) return this.retake();
      return undefined;
    }

    // Arrow keys move between the answers of a question, like radio buttons
    onKeydown(event) {
      const option = event.target instanceof Element ? event.target.closest('.ape-quiz__option') : null;
      const moves = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 };
      if (!option || !(event.key in moves)) return;
      event.preventDefault();
      const options = [...option.parentElement.children];
      const next = options[(options.indexOf(option) + moves[event.key] + options.length) % options.length];
      options.forEach((item) => {
        item.tabIndex = item === next ? 0 : -1;
      });
      next.focus();
    }

    onPopState(event) {
      const state = event.state;
      if (!state || !state.apronQuiz) return;
      let step = state.apronQuiz;
      // Only show a question once the ones before it are answered (e.g. after a page refresh)
      const firstMissing = QUESTIONS.findIndex((key) => !this.answers[key]);
      if (firstMissing !== -1 && step > firstMissing + 1) step = firstMissing + 1;
      this.go(step, step < this.step ? -1 : 1, false);
    }

    // Theme editor: selecting an answer block shows its question
    onBlockSelect(event) {
      const panel = event.target instanceof Element ? event.target.closest('[data-step]') : null;
      if (panel && this.contains(panel)) this.go(Number(panel.dataset.step), 1, false);
    }

    /* ---------- Questions ---------- */

    choose(option) {
      const group = option.closest('[data-question]');
      const key = group.dataset.question;
      [...group.children].forEach((item) => {
        const selected = item === option;
        item.setAttribute('aria-checked', String(selected));
        item.tabIndex = selected ? 0 : -1;
      });
      this.answers[key] = option.dataset.value;
      this.result = null;
      this.updateChrome();
      if (key === 'priority') this.warmUp();
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.next(), ADVANCE_DELAY);
    }

    next() {
      clearTimeout(this.timer);
      if (this.step > 3 || !this.answers[QUESTIONS[this.step - 1]]) return;
      if (this.step === 3 && !this.match()) return;
      this.go(this.step + 1, 1, true);
    }

    back() {
      if (this.step === 1) {
        this.close();
        return;
      }
      if (!designMode() && history.state && history.state.apronQuizIndex > 0) {
        history.back();
        return;
      }
      this.go(this.step - 1, -1, false);
    }

    close(event) {
      let fromStore = false;
      try {
        const referrer = new URL(document.referrer);
        fromStore = referrer.origin === window.location.origin && referrer.pathname !== window.location.pathname;
      } catch (error) {
        /* arrived from outside the store */
      }
      if (fromStore) {
        if (event) event.preventDefault();
        window.location.assign(document.referrer);
      } else if (!event) {
        const link = this.querySelector('[data-quiz-close]');
        window.location.assign(link ? link.href : '/');
      }
      // Otherwise the X link itself goes to the close link
    }

    go(step, direction, push) {
      clearTimeout(this.timer);
      const from = this.steps.find((panel) => !panel.hidden);
      const to = this.steps.find((panel) => Number(panel.dataset.step) === step);
      if (!to) return;
      if (step === 4) {
        if (!this.match()) return;
        this.showSet(this.current);
      }
      if (from && from !== to) {
        from.hidden = true;
        to.hidden = false;
        to.classList.remove('is-forward', 'is-back');
        void to.offsetWidth;
        to.classList.add(direction < 0 ? 'is-back' : 'is-forward');
      }
      this.step = step;
      this.updateChrome();
      if (push && !designMode()) {
        const index = ((history.state && history.state.apronQuizIndex) || 0) + 1;
        history.pushState({ ...(history.state || {}), apronQuiz: step, apronQuizIndex: index }, '');
      }
      this.scrollToTop(false);
      const heading = to.querySelector('[tabindex="-1"]');
      if (heading) heading.focus({ preventScroll: true });
      if (step === 4) this.completed();
    }

    updateChrome() {
      const onMatch = this.step === 4;
      const progress = onMatch ? 100 : Math.round((this.step / 3) * 100);
      this.ui.stepLabel.textContent = onMatch ? this.strings.result : fill(this.strings.step, { n: this.step });
      this.ui.fill.style.width = `${progress}%`;
      this.ui.track.setAttribute('aria-valuenow', String(progress));
      this.ui.footer.hidden = onMatch;
      this.ui.next.disabled = onMatch || !this.answers[QUESTIONS[this.step - 1]];
      this.classList.toggle('is-match', onMatch);
    }

    scrollToTop(smooth) {
      const top = Math.max(0, this.getBoundingClientRect().top + window.scrollY);
      window.scrollTo({ top, behavior: smooth && !reducedMotion() ? 'smooth' : 'auto' });
    }

    // After the priority answer, start loading the photos the match is likely to show
    warmUp() {
      const priority = this.data.priorities[this.answers.priority];
      const role = this.data.roles[this.answers.role];
      if (!priority) return;
      const material = priority.material !== 'any' ? priority.material : role && role.material;
      const seen = new Set();
      this.sets
        .filter((set) => set.material === material)
        .forEach((set) => {
          if (seen.has(set.family)) return;
          seen.add(set.family);
          preload(set.image);
        });
    }

    /* ---------- The match ---------- */

    match() {
      if (this.result) return this.result;
      const role = this.data.roles[this.answers.role];
      const priority = this.data.priorities[this.answers.priority];
      const straps = this.data.straps[this.answers.strap];
      if (!role || !priority || !straps || !this.sets.length) return null;

      const material = priority.material !== 'any' ? priority.material : role.material;
      const strap = straps.strap !== 'any' ? straps.strap : role.strap;
      const best = (list, score) =>
        list
          .map((set) => ({ set, score: score(set) }))
          .sort((a, b) => b.score - a.score || a.set.index - b.set.index)[0];

      // Straps matter most (they asked for them by name), then the material. Ties go to the first set
      // in the editor's order, so the order of the Set blocks sets the default colour.
      const main = best(this.sets, (set) => (set.strap === strap ? 3 : 0) + (set.material === material ? 2 : 0));
      if (!main) return null;
      // The alternative is a different kind of set: ideally the other material with the same straps
      const others = this.sets.filter((set) => set.family !== main.set.family);
      const alt = best(
        others,
        (set) => (set.material !== main.set.material ? 2 : 0) + (set.strap === strap ? 3 : 0) + (set.strap === main.set.strap ? 1 : 0)
      );

      this.result = { role, priority, straps };
      this.current = main.set;
      this.alternative = alt ? alt.set : null;
      return this.result;
    }

    matchTitle(set) {
      const family = this.data.families[set.family] || {};
      return fill(family.title || set.product, { colour: set.apron });
    }

    showSet(set, { keepColours = false } = {}) {
      const result = this.match();
      if (!set || !result) return;
      this.current = set;
      const family = this.data.families[set.family] || {};
      const role = result.role;
      const saving = set.compare > set.price ? set.compare - set.price : 0;

      this.el('eyebrow').textContent = fill(this.strings.eyebrow, { role: role.title });
      this.el('title').textContent = this.matchTitle(set);

      const image = this.el('image');
      if (set.image && image.getAttribute('src') !== set.image) {
        image.classList.add('is-loading');
        image.onload = () => image.classList.remove('is-loading');
        image.onerror = () => image.classList.remove('is-loading');
        image.src = set.image;
      }
      image.alt = set.alt || set.product;
      const inset = this.el('inset');
      inset.hidden = !set.inset;
      if (set.inset) inset.src = set.inset;
      const badge = this.el('badge');
      badge.hidden = !saving;
      badge.textContent = saving ? fill(this.strings.badge, { amount: roundMoney(saving) }) : '';

      this.el('why').textContent = fill(this.strings.why, { role: role.short || role.title });
      const reasons = [role.why, ...(family.lines || [])]
        .map((line) => String(line || '').trim())
        .filter(Boolean)
        .slice(0, 4);
      this.el('lines').replaceChildren(
        ...reasons.map((line) => {
          const item = document.createElement('li');
          const text = document.createElement('span');
          text.textContent = line;
          item.append(this.ui.check.content.cloneNode(true), text);
          return item;
        })
      );

      this.el('price').textContent = money(set.price);
      const compare = this.el('compare');
      compare.hidden = !saving;
      compare.textContent = saving ? money(set.compare) : '';
      const save = this.el('save');
      save.hidden = !saving;
      save.textContent = saving ? fill(this.strings.save, { amount: roundMoney(saving) }) : '';
      this.renderAfterpay(set.price);

      this.el('variant').value = set.variant;

      const siblings = this.sets.filter((item) => item.family === set.family);
      const hasColours = siblings.length > 1;
      this.ui.colours.hidden = !hasColours;
      this.ui.details.hidden = hasColours;
      this.ui.details.href = set.url;
      const swatches = this.el('swatches');
      swatches.replaceChildren(
        ...siblings.map((item) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'ape-quiz__swatch';
          button.dataset.set = String(item.index);
          button.setAttribute('aria-pressed', String(item === set));
          button.textContent = item.swatch;
          return button;
        })
      );
      if (!keepColours || !hasColours) {
        swatches.hidden = true;
        this.ui.colours.setAttribute('aria-expanded', 'false');
      }

      this.renderAlternative();
    }

    renderAfterpay(price) {
      const line = this.el('afterpay');
      const show = this.settings.afterpay && AFTERPAY_CURRENCIES.includes(currency()) && price > 0;
      line.hidden = !show;
      if (!show) return;
      const [before, after = ''] = String(this.strings.afterpay || '').split('[amount]');
      const tail = after.trimEnd();
      const cut = tail.lastIndexOf(' ') + 1;
      const amount = document.createElement('strong');
      amount.textContent = money(Math.ceil(price / 4));
      const info = document.createElement('a');
      info.href = AFTERPAY_INFO;
      info.target = '_blank';
      info.rel = 'noopener';
      info.setAttribute('aria-label', 'How Afterpay works (opens in a new tab)');
      info.innerHTML = '<svg class="ape-quiz-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5"/><path d="M12 11.2v5M12 7.9v.01"/></svg>';
      // The last word and the info icon stay together, so the icon never sits alone on a line
      const lastWord = document.createElement('span');
      lastWord.className = 'ape-quiz__nowrap';
      lastWord.append(tail.slice(cut), info);
      line.replaceChildren(before, amount, tail.slice(0, cut), lastWord);
    }

    renderAlternative() {
      const alt = this.alternative;
      this.ui.alt.hidden = !alt;
      if (!alt) return;
      const family = this.data.families[alt.family] || {};
      const siblings = this.sets.filter((item) => item.family === alt.family);
      const lowest = Math.min(...siblings.map((item) => item.price));
      const image = this.el('alt-image');
      image.src = alt.thumb || alt.image || '';
      // The teaser names what is different. A different material uses the TPU + OG or leather + OG
      // teaser ("Need it splash-proof?" / "Prefer a softer look?"); same material, the set's own teaser.
      let teaser = family.teaser;
      if (this.current && alt.material !== this.current.material) {
        const materialFamily = this.data.families[alt.material === 'tpu' ? 'tpu_og' : 'leather_og'] || {};
        teaser = materialFamily.teaser || teaser;
      }
      this.el('alt-teaser').textContent = teaser || '';
      this.el('alt-name').textContent = family.name || alt.product;
      this.el('alt-meta').textContent = fill(siblings.length > 1 ? this.strings.setMeta : this.strings.setMetaSingle, {
        price: money(lowest),
        count: siblings.length,
      });
    }

    toggleColours() {
      const swatches = this.el('swatches');
      const open = swatches.hidden;
      swatches.hidden = !open;
      this.ui.colours.setAttribute('aria-expanded', String(open));
    }

    swapAlternative() {
      if (!this.alternative) return;
      const next = this.alternative;
      this.alternative = this.current;
      this.showSet(next);
      const panel = this.steps.find((item) => item.dataset.step === '4');
      panel.classList.remove('is-forward', 'is-back');
      void panel.offsetWidth;
      panel.classList.add('is-forward');
      this.scrollToTop(true);
      this.el('title').focus({ preventScroll: true });
    }

    retake() {
      this.answers = {};
      this.result = null;
      this.current = null;
      this.alternative = null;
      this.querySelectorAll('[data-question]').forEach((group) => {
        [...group.children].forEach((option, index) => {
          option.setAttribute('aria-checked', 'false');
          option.tabIndex = index === 0 ? 0 : -1;
        });
      });
      this.ui.emailForm.hidden = false;
      this.ui.emailDone.hidden = true;
      this.go(1, -1, true);
    }

    /* ---------- Cart ---------- */

    addToCart(event) {
      event.preventDefault();
      if (!this.el('variant').value) return;
      const button = this.ui.cart.querySelector('[data-add-to-cart]');
      const cartEvent = new CustomEvent('theme:cart:add', { detail: { button }, bubbles: true, cancelable: true });
      document.dispatchEvent(cartEvent);
      // No theme cart on the page: add to cart the plain way
      if (!cartEvent.defaultPrevented) this.ui.cart.submit();
      this.publish('apron_quiz_add_to_cart', { ...this.answerLabels(), match: this.matchTitle(this.current) });
    }

    /* ---------- Klaviyo + analytics ---------- */

    answerLabels() {
      const result = this.match();
      if (!result) return {};
      return {
        role: result.role.value || result.role.title,
        priority: result.priority.value || result.priority.title,
        straps: result.straps.value || result.straps.title,
      };
    }

    profileProperties() {
      const answers = this.answerLabels();
      return {
        'Salon role': answers.role,
        'Apron quiz priority': answers.priority,
        'Apron quiz straps': answers.straps,
        'Apron quiz match': this.matchTitle(this.current),
        'Apron quiz match URL': absolute(this.current.url),
        'Apron quiz date': new Date().toISOString(),
      };
    }

    eventProperties() {
      const answers = this.answerLabels();
      const set = this.current;
      const alt = this.alternative;
      const altFamily = alt ? this.data.families[alt.family] || {} : {};
      return {
        Role: answers.role,
        Priority: answers.priority,
        Straps: answers.straps,
        Match: this.matchTitle(set),
        'Match colour': set.swatch,
        'Match URL': absolute(set.url),
        'Match image': absolute(set.image),
        'Match price': money(set.price),
        Alternative: alt ? altFamily.name || alt.product : '',
        'Alternative URL': alt ? absolute(alt.url) : '',
        'Alternative image': alt ? absolute(alt.image) : '',
        Source: this.settings.source || 'Find your apron quiz',
      };
    }

    publish(name, detail) {
      try {
        const analytics = window.Shopify && window.Shopify.analytics;
        if (analytics && typeof analytics.publish === 'function') analytics.publish(name, detail);
      } catch (error) {
        /* analytics is a nice-to-have */
      }
    }

    // Called once per set of answers, when the match first shows
    completed() {
      const result = this.result;
      if (!result || result.reported) return;
      result.reported = true;
      this.publish('apron_quiz_completed', { ...this.answerLabels(), match: this.matchTitle(this.current) });
      const klaviyo = window.klaviyo;
      if (!klaviyo || typeof klaviyo.isIdentified !== 'function' || typeof klaviyo.identify !== 'function') return;
      Promise.resolve(klaviyo.isIdentified())
        .then((known) => {
          if (known) klaviyo.identify(this.profileProperties());
        })
        .catch(() => {});
    }

    emailMessage(text) {
      this.ui.emailError.textContent = text;
      this.ui.emailError.hidden = !text;
      this.ui.emailForm.elements.email.setAttribute('aria-invalid', text ? 'true' : 'false');
    }

    async subscribe(company, email, withProperties) {
      const attributes = { email };
      if (withProperties) attributes.properties = this.profileProperties();
      return klaviyoPost('subscriptions', company, {
        type: 'subscription',
        attributes: {
          custom_source: this.settings.source || 'Find your apron quiz',
          profile: { data: { type: 'profile', attributes } },
        },
        relationships: { list: { data: { type: 'list', id: this.settings.listId } } },
      });
    }

    async submitEmail(event) {
      event.preventDefault();
      const form = this.ui.emailForm;
      const input = form.elements.email;
      const email = input.value.trim();
      if (!EMAIL_PATTERN.test(email)) {
        this.emailMessage(this.strings.invalid);
        input.focus();
        return;
      }
      const company = klaviyoCompany(this.settings);
      if (!company || !this.settings.listId || !this.match()) {
        this.emailMessage(this.strings.failed);
        return;
      }
      this.emailMessage('');
      const button = form.querySelector('[type="submit"]');
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      try {
        let response = await this.subscribe(company, email, true);
        // Fall back to a plain sign-up if Klaviyo ever refuses the extra details; the event below
        // still saves them on the profile.
        if (response.status === 400) response = await this.subscribe(company, email, false);
        if (!response.ok) throw new Error(`Klaviyo ${response.status}`);

        await klaviyoPost('events', company, {
          type: 'event',
          attributes: {
            properties: this.eventProperties(),
            metric: { data: { type: 'metric', attributes: { name: 'Completed Apron Quiz' } } },
            profile: { data: { type: 'profile', attributes: { email, properties: this.profileProperties() } } },
          },
        }).catch(() => null);

        try {
          if (window.klaviyo && typeof window.klaviyo.identify === 'function') window.klaviyo.identify({ email });
        } catch (error) {
          /* identifying the browser is a nice-to-have */
        }
        this.publish('apron_quiz_email', this.answerLabels());

        this.el('saved-text').textContent = fill(this.strings.saved, { email });
        form.hidden = true;
        this.ui.emailDone.hidden = false;
        const heading = this.ui.emailDone.querySelector('[tabindex="-1"]');
        if (heading) heading.focus();
      } catch (error) {
        this.emailMessage(this.strings.failed);
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('apron-quiz', ApronQuiz);
})();
